import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { TaskCardDrawer } from '~/components/tasks/card/TaskCardDrawer';
import { TaskCardFullPage } from '~/components/tasks/card/TaskCardFullPage';
import { ProjectLayoutRoot } from '~/components/tasks/views/ProjectLayoutRoot';
import { useAuthStore } from '~/store/auth';
import { useUiStore } from '~/store/ui';
import shared from './_auth._shared.module.css';

/**
 * `/work-tasks/$projectId/task/$taskId` — карточка задачи (FE-S12.3-16).
 *
 * ?mode=drawer — overlay поверх board/list (background сохраняется);
 * default — full-page (прямой URL / почта / уведомление).
 */

const SearchSchema = z.object({
  mode: z.enum(['drawer', 'page']).optional().catch(undefined),
});

export const Route = createFileRoute('/_auth/work-tasks/$projectId_/task/$taskId')({
  staticData: { minMainWidth: 792 },
  validateSearch: SearchSchema,
  component: TaskCardRoute,
});

function TaskCardRoute(): JSX.Element {
  const { projectId, taskId } = Route.useParams();
  // F20 — id актора для карточки: без него корзина вложений и claim-логика
  // не знают, «я» это или нет (проп годами дефолтил в пустую строку).
  const currentUserId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? '');
  const { mode } = Route.useSearch();
  const navigate = useNavigate();

  // UX-SSOT §8/§131 — Designing for Intent: SideNav автосворачивается при входе
  // в detail-задачу (intent = одна задача). Возвращает ~180px левого гаттера,
  // экран перестаёт читаться как недозаполненный. Уважаем ручной re-expand.
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const weCollapsedRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount/unmount only
  useEffect(() => {
    if (!sidebarCollapsed) {
      setSidebarCollapsed(true);
      weCollapsedRef.current = true;
    }
    return () => {
      if (weCollapsedRef.current) setSidebarCollapsed(false);
    };
  }, []);

  function closeDrawer() {
    void navigate({ to: '/work-tasks/$projectId', params: { projectId } });
  }

  if (mode === 'drawer') {
    return (
      <div className={shared.page}>
        <ProjectLayoutRoot projectId={projectId} />
        <TaskCardDrawer
          taskId={taskId}
          projectId={projectId}
          currentUserId={currentUserId}
          onClose={closeDrawer}
        />
      </div>
    );
  }

  // FE-4 — full-page карточка сама заполняет рабочую область (root .fill = flex:1);
  // обёртка shared.page (flex:0 1 auto) схлопывала её к ширине контента → мёртвая зона.
  return <TaskCardFullPage taskId={taskId} projectId={projectId} currentUserId={currentUserId} />;
}
